import { Account, ComplianceEntityType } from "@wingspanhq/wingspan-api-sdk";

// Company structures Wingspan treats as an individual rather than a business —
// mirrors the list wsName uses for members.
const INDIVIDUAL_COMPANY_STRUCTURES = [
  "None",
  "SoleProprietorship",
  "LlcSingleMember"
];

// The v3 Account types `profile` as a freeform object, so the name-bearing
// fields are narrowed locally to what this selector reads (see AccountProfile
// in the v3 API spec).
type AccountNameProfile = {
  displayName?: string;
  individual?: {
    firstName?: string;
    lastName?: string;
  };
  company?: {
    legalBusinessName?: string;
    doingBusinessAs?: string;
    structure?: string;
  };
};

const getIsIndividual = (structure?: string): boolean =>
  !structure || INDIVIDUAL_COMPANY_STRUCTURES.includes(structure);

// Resolves a human-readable display name for a v3 Account, mirroring the
// resolution wsName applies to a member (minus the person/email side, which an
// Account has no equivalent for): an explicit presentation `displayName` wins,
// then the company name (legal / DBA) or the individual's full name depending
// on the company structure, falling back to the same fields on the account's
// complianceEntity (populated even when `profile` hasn't been filled in yet,
// e.g. mid-onboarding — see selectorPersonDisplayName for the Person analog),
// and finally the account id as a last-resort fallback.
export const selectorAccountDisplayName = (account: Account): string => {
  const profile = (account.profile ?? {}) as AccountNameProfile;
  const complianceEntity = account.complianceEntity;

  const displayName = profile.displayName?.trim();
  if (displayName) {
    return displayName;
  }

  const companyName =
    profile.company?.legalBusinessName?.trim() ||
    profile.company?.doingBusinessAs?.trim() ||
    complianceEntity?.legalName?.trim() ||
    complianceEntity?.doingBusinessAs?.trim();

  const individualLegalName = complianceEntity?.individualLegalName;
  const fullName =
    [profile.individual?.firstName, profile.individual?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() ||
    individualLegalName?.fullLegalName?.trim() ||
    [individualLegalName?.givenName, individualLegalName?.familyName]
      .filter(Boolean)
      .join(" ")
      .trim();

  const isIndividual = complianceEntity?.type
    ? complianceEntity.type === ComplianceEntityType.INDIVIDUAL
    : getIsIndividual(profile.company?.structure);

  if (isIndividual) {
    if (companyName) {
      return fullName ? `${companyName} (${fullName})` : companyName;
    }
    if (fullName) {
      return fullName;
    }
  } else if (companyName) {
    return companyName;
  }

  const preferredName = complianceEntity?.preferredName?.trim();
  if (preferredName) {
    return preferredName;
  }

  return account.id ?? "";
};
